import React, { useState } from 'react';
import useUrlState from '../../../../hooks/useUrlState';
import { FinanceInfluencerPayment, payCreator, PaymentMethods } from '@round/api';
import useAbortableEffect from '../../../../hooks/useAbortableEffect';
import useOnScroll from '../../../../hooks/useOnScroll';
import { formatToIsoDateString, mapApiErrorsToFormikErrors } from '../../../../utils/utits';
import styles from '../CreatorPayments.module.css';
import DatePickerSelect from '../../../../ui/DataEntry/DatePicker/DatePickerSelect/DatePickerSelect';
import { Button, Modal, ModalFooter, ModalTitle, Select, Skeleton } from '@round/ui-kit';
import CreatorPaymentsTable from './CreatorPaymentsTable/CreatorPaymentsTable';
import { showNotification } from '../../../../utils/showNotification';
import { DebounceInput } from 'react-debounce-input';
import useFinanceManagerSelect from '../useFinanceManagerSelect';
import { filterSelectStyles } from '../helpers';
import useCreatorSelect from '../../../influencer/useCreatorSelect';
import { patchFinanceBooking } from '@round/api';
import { PaymentTerms } from '@round/api';
import { useOutstandingPayments } from './useOutstandingPayments';

type UrlState = {
    payment_due_start_date: string;
    payment_due_end_date: string;
    booking_id: number;
    manager_id: number;
    influencer_id: number;
};

const OutstandingPayments = () => {
    const [urlState, setUrlState] = useUrlState<UrlState>({}, { manager_id: 'number', influencer_id: 'number' });

    const [paymentConfirmationData, setPaymentConfirmationData] = useState<{
        paymentId: number;
        method: PaymentMethods;
    } | null>(null);
    const showPaidByConfirmationModal = Boolean(paymentConfirmationData);
    const [paymentConfirmationLoading, setPaymentConfirmationLoading] = useState(false);
    const [confirmedPayments, setConfirmedPayments] = useState<FinanceInfluencerPayment[]>([]);

    const selectedDueDateRange: [Date, Date] | null =
        urlState.payment_due_start_date && urlState.payment_due_end_date
            ? [new Date(urlState.payment_due_start_date), new Date(urlState.payment_due_end_date)]
            : null;

    const managerSelectProps = useFinanceManagerSelect(urlState.manager_id);
    const influencerSelectProps = useCreatorSelect(urlState.influencer_id);

    const {
        payments: outstandingCreatorPayments,
        totals,
        isInitialized,
        isLoading,
        hasNextPage,
        count,
        init,
        loadNextPage,
        reset,
        refreshPayment,
    } = useOutstandingPayments({
        page_size: 30,
        payment_due_start_date: urlState.payment_due_start_date,
        payment_due_end_date: urlState.payment_due_end_date,
        booking_id: urlState.booking_id,
        manager_id: urlState.manager_id,
        influencer_id: urlState.influencer_id,
    });

    useAbortableEffect(
        (signal) => {
            if (!isInitialized) {
                init({ signal });
            }
        },
        [isInitialized, init]
    );

    const handleOutstandingBottomReached = useOnScroll(() => {
        if (!isLoading && hasNextPage) {
            loadNextPage();
        }
    });

    const outstandingPaymentsRowCountLabel = count ? `Showing ${outstandingCreatorPayments.length} of ${count}` : null;

    const handlePaidBy = async () => {
        if (!paymentConfirmationData) {
            return;
        }

        try {
            setPaymentConfirmationLoading(true);
            const response = await payCreator(paymentConfirmationData.paymentId, paymentConfirmationData.method);
            if (response.status === 201) {
                showNotification('Success', 'info');
                setConfirmedPayments((payments) => payments.concat(response.data));
            }
        } catch {
            showNotification('Failed to perform operation', 'error');
        } finally {
            setPaymentConfirmationData(null);
            setPaymentConfirmationLoading(false);
        }
    };

    const handleUpdatePaymentTerms = async (bookingId: number, paymentTerms: PaymentTerms) => {
        try {
            const response = await patchFinanceBooking(bookingId, { payment_terms: paymentTerms });

            if (response.status === 200) {
                showNotification('Updated payment terms', 'info');
                refreshPayment(bookingId);
                return;
            }

            const errorMessage =
                response.status === 400
                    ? mapApiErrorsToFormikErrors(response.data).payment_terms
                    : response.data.detail;

            showNotification(errorMessage || 'Failed to update payment terms', 'error');
        } catch (e) {
            showNotification('Failed to update payment terms', 'error');
            throw e;
        }
    };

    return (
        <>
            <div className={styles.panelHeader}>
                <div className={styles.filters}>
                    <div className={styles.filter}>
                        <span className={styles.label}>Due Date</span>
                        <DatePickerSelect
                            selectRange
                            value={selectedDueDateRange}
                            onChange={(dates) => {
                                if (dates.length === 2) {
                                    const [startDate, endDate] = dates;
                                    setUrlState({
                                        payment_due_start_date: formatToIsoDateString(startDate),
                                        payment_due_end_date: formatToIsoDateString(endDate),
                                    });
                                    reset();
                                    return;
                                }
                            }}
                            onClear={() => {
                                setUrlState({ payment_due_start_date: undefined, payment_due_end_date: undefined });
                                reset();
                            }}
                            className={styles.filterInput}
                        />
                    </div>

                    <div className={styles.filter}>
                        <span className={styles.label}>Booking ID</span>
                        <DebounceInput
                            type="number"
                            className={styles.filterInput}
                            placeholder="Enter booking id..."
                            value={urlState.booking_id}
                            onChange={(e) => {
                                setUrlState({ booking_id: e.target.value ? parseInt(e.target.value) : undefined });
                                reset();
                            }}
                            debounceTimeout={700}
                        />
                    </div>

                    <div className={styles.filter}>
                        <span className={styles.label}>Manager</span>
                        <Select
                            isClearable
                            styles={filterSelectStyles}
                            placeholder="Search manager..."
                            {...managerSelectProps}
                            onChange={(value, action) => {
                                if (typeof managerSelectProps.onChange === 'function') {
                                    managerSelectProps.onChange(value, action);
                                }
                                setUrlState({ manager_id: value?.value });
                                reset();
                            }}
                        />
                    </div>

                    <div className={styles.filter}>
                        <span className={styles.label}>Creator</span>
                        <Select
                            isClearable
                            styles={filterSelectStyles}
                            placeholder="Search creator..."
                            {...influencerSelectProps}
                            onChange={(value, action) => {
                                if (typeof influencerSelectProps.onChange === 'function') {
                                    influencerSelectProps.onChange(value, action);
                                }
                                setUrlState({ influencer_id: value?.value });
                                reset();
                            }}
                        />
                    </div>
                </div>

                <span className={styles.label}>{isLoading ? <Skeleton /> : outstandingPaymentsRowCountLabel}</span>
            </div>

            <div className={styles.tableScrollContainer} onScroll={handleOutstandingBottomReached}>
                <CreatorPaymentsTable
                    data={outstandingCreatorPayments.filter(
                        (p) => !confirmedPayments.find((confirmedPayment) => confirmedPayment.booking_id === p.id)
                    )}
                    onPaidByClicked={(paymentId, method) => {
                        setPaymentConfirmationData({ paymentId, method });
                    }}
                    updatePaymentTerms={handleUpdatePaymentTerms}
                    totals={totals}
                    loading={isLoading}
                />
            </div>

            <Modal
                closeOnOverlayClick
                isOpen={showPaidByConfirmationModal}
                onClose={() => {
                    if (!paymentConfirmationLoading) {
                        setPaymentConfirmationData(null);
                    }
                }}
                className={styles.modal}
            >
                <ModalTitle>
                    Mark as paid by {paymentConfirmationData?.method === 'bank_transfer' ? 'bank' : 'PayPal'}?
                </ModalTitle>

                <ModalFooter align="left">
                    <Button loading={paymentConfirmationLoading} type="filled" color="black" onClick={handlePaidBy}>
                        Confirm
                    </Button>

                    <Button disabled={paymentConfirmationLoading} onClick={() => setPaymentConfirmationData(null)}>
                        Cancel
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    );
};

export default OutstandingPayments;
